import { useMemo } from 'react'
import axios from 'axios'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useProfileLookup } from './profile_lookup'

export const ROLES = {
  SUPERADMIN: 'SUPERADMIN',
  EF_ADMIN: 'EF_ADMIN',
  USER_ADMIN: 'USER_ADMIN',
  CONSULTANT: 'CONSULTANT',
}

export const ROLE_LABELS = {
  SUPERADMIN: 'Super Admin',
  EF_ADMIN: 'EF Admin',
  USER_ADMIN: 'User Admin',
  CONSULTANT: 'Consultant',
}

const API = process.env.REACT_APP_API_URL

export function useAdminRoles() {
  const { data: profile, isLoading } = useProfileLookup()

  return useMemo(() => {
    const roles = Array.isArray(profile?.adminRoles) ? profile.adminRoles : []
    const isSuperAdmin = roles.includes(ROLES.SUPERADMIN)
    return {
      isLoading,
      roles,
      isSuperAdmin,
      hasAdmin: roles.length > 0,
      has(role) {
        return isSuperAdmin || roles.includes(role)
      },
      hasAny(...wanted) {
        return isSuperAdmin || wanted.some(r => roles.includes(r))
      },
    }
  }, [profile, isLoading])
}

export function useAdminRolesLookup() {
  return useQuery({
    queryKey: ['admin-roles-lookup'],
    queryFn: async ({ signal }) => {
      return await axios({
        url: [API, '/api-admin/v2/admin-roles-lookup'].join(''),
        method: 'GET',
        withCredentials: true,
        signal,
      }).then(res => res?.data?.data ?? [])
    },
    retry: false,
  })
}

export function useAdminRoleAssign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId, role }) => {
      return await axios({
        url: [API, '/api-admin/v2/admin-roles-assign'].join(''),
        method: 'POST',
        withCredentials: true,
        data: { userId, role },
      }).then(res => res?.data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-roles-lookup'] })
    },
  })
}

export function useAdminRoleRevoke() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId, role }) => {
      return await axios({
        url: [API, '/api-admin/v2/admin-roles-revoke'].join(''),
        method: 'POST',
        withCredentials: true,
        data: { userId, role },
      }).then(res => res?.data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-roles-lookup'] })
    },
  })
}
